"use client"

// Layer H (docs/30 §4): the inverted SSR island, extracted 1:1 from the std
// slot's MsgDeskStdClient and generalized by the surface seam — the desk
// view arrives as the surface's RoutedPage/Loading components instead of a
// static std import. Everything else is the S1 architecture unchanged
// (docs/27 §12): the desk view and app host are static server-rendered
// imports; only the runtime (whose legacy engine is its own lazy chunk
// inside MsgDeskRuntime.boot()) is booted dynamically after hydration.

import { useEffect, useMemo, useState, type ComponentType } from 'react'
import { installMsgDeskAppBridge } from '@/msgdeskRuntime/appBridge'
import { INITIAL_PHP_MODEL_PREFIX } from '@/msgdeskRuntime/legacyBridge/legacyRequestParams'
import {
  payloadToState,
  treeStore,
  type DeskBootMode,
  type SubsStoreState,
} from '@/msgdeskRuntime/state'
// PinDialog is a skeleton (Layer B) widget — shared, not surface-owned.
import { showPinDialog } from '@/deskUiSkeletons/Shared/PinDialog'
import Template2App, { type MsgDeskPageProps, type Template2AppProps } from '../MsgDeskApp'
import type { StandardRouteMode } from './deskRoutes'
import type { StandardEditorMode } from '../routing/standard'
import type { DeskSurfaceClientProps } from './surfaceDescriptor'

export type DeskSurfaceRoutedPageProps = MsgDeskPageProps & {
  initialMode?: StandardRouteMode
  initialEditorMode?: StandardEditorMode
}

export interface MsgDeskSurfaceClientProps extends DeskSurfaceClientProps {
  RoutedPage: ComponentType<DeskSurfaceRoutedPageProps>
  Loading: ComponentType
  /**
   * Whether the plain msg route runs the legacy initial-subs load
   * (facade.loadInitialSubs). Reading-pane surfaces whose msg route must make
   * no subs request and never toggle the root sections open pass false; their
   * folder routes still make their own targeted section request post-boot.
   */
  loadInitialSubsOnMsgRoute?: boolean
}

let treeStoreSeeded = false

function seedTreeStoreOnce(initialState: SubsStoreState | null) {
  // Client only, synchronously during the first render pass — BEFORE the
  // provider reads the singleton — so hydration matches the server HTML.
  // The server must never seed the shared singleton (per-request state goes
  // through the provider's initialState prop instead).
  if (typeof window === 'undefined' || treeStoreSeeded || !initialState) return
  treeStoreSeeded = true
  treeStore.seedInitialState(initialState)
}

function deskBootMode(initialMode: StandardRouteMode): DeskBootMode {
  return initialMode
}

function canPrerenderPayload(payload: string | null | undefined): payload is string {
  return !!payload
    && !payload.startsWith('to_link_')
    && !payload.startsWith(INITIAL_PHP_MODEL_PREFIX)
}

export default function MsgDeskSurfaceClient({
  params,
  isTestMode,
  initialLegacyHtml,
  initialMode = 'msg',
  initialEditorMode,
  sectionSpans,
  collectionSpans,
  initialPooProtocol,
  RoutedPage,
  Loading,
  loadInitialSubsOnMsgRoute = true,
}: MsgDeskSurfaceClientProps) {
  const initialState = useMemo(() => (
    canPrerenderPayload(initialLegacyHtml)
      ? payloadToState(initialLegacyHtml, {
        mode: deskBootMode(initialMode),
        sectionSpans,
        collectionSpans,
        pooProtocol: initialPooProtocol,
      })
      : null
  ), [collectionSpans, initialLegacyHtml, initialMode, initialPooProtocol, sectionSpans])

  seedTreeStoreOnce(initialState)

  const [runtimeReady, setRuntimeReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    import('@/msgdeskRuntime/boot')
      .then(({ bootMsgDeskRuntime }) => bootMsgDeskRuntime({
        surface: 'template2',
        params,
        readParamsFromHost: false,
      }))
      .then(({ runtime }) => {
        if (cancelled) return
        if (isTestMode) runtime.setGlobal('SET_IS_TEST_MODE', true)
        installMsgDeskAppBridge(runtime.surface)
        setRuntimeReady(true)
      })
      .catch((bootError) => {
        if (cancelled) return
        setError(bootError instanceof Error ? bootError.message : String(bootError))
      })

    return () => {
      cancelled = true
    }
  }, [isTestMode, params])

  if (error) return <div data-md-contract="next.host.error">{error}</div>

  const PageComponent: Template2AppProps['PageComponent'] = (props) => (
    <RoutedPage {...props} initialMode={initialMode} initialEditorMode={initialEditorMode} />
  )
  const loadInitialSubs = loadInitialSubsOnMsgRoute && initialMode === 'msg' && !initialEditorMode
  return (
    <Template2App
      initialLegacyHtml={initialLegacyHtml}
      initialState={initialState}
      initialDeskMode={initialEditorMode ? 'msg' : deskBootMode(initialMode)}
      runtimeReady={runtimeReady}
      PageComponent={PageComponent}
      loadInitialSubs={loadInitialSubs}
      LoadingComponent={Loading}
      showPinDialog={showPinDialog}
      collectionSpans={collectionSpans}
    />
  )
}
